import { ApiResponse } from '../types/ApiResponse';
import { BlobApiResponse } from '../types/BlobApiResponse';
import { getFetchWrapper } from './lib/fetchWrapper';

export const getData = async <InputType, OutputType = InputType>(
  url: string,
  headers: Headers,
  transformer?: (input: InputType) => OutputType
): Promise<ApiResponse<OutputType>> => {
  const response = await performRequest('GET', url, undefined, headers);
  if (response.data && transformer) {
    response.data = transformer(response.data);
  }
  return response;
};

export const getBlobData = async (
  url: string,
  headers: Headers
): Promise<BlobApiResponse> => {
  const res = await getFetchWrapper().fetch(url, {
    method: 'GET',
    headers,
    credentials: 'include',
    mode: 'cors',
  });
  try {
    const data = await res.clone().blob();
    return new BlobApiResponse(res.status, data);
  } catch {
    const error = await res.text();
    return new BlobApiResponse(res.status, undefined, error);
  }
};

export const postData = async <T>(
  url: string,
  data: Record<string, any> = {},
  headers: Headers
): Promise<ApiResponse<T>> => post(url, JSON.stringify(data), headers);

export const postForm = async <T>(
  url: string,
  formData: FormData,
  headers: Headers
): Promise<ApiResponse<T>> => post(url, formData, headers);

const post = async <T>(
  url: string,
  body: FormData | string,
  headers: Headers
): Promise<ApiResponse<T>> => performRequest('POST', url, body, headers);

export const putData = async <T>(
  url: string,
  body: Record<string, any>,
  headers: Headers
): Promise<ApiResponse<T>> =>
  performRequest('PUT', url, JSON.stringify(body), headers);

export const deleteData = async <T>(
  url: string,
  body: Record<string, any> | any[],
  headers: Headers
): Promise<ApiResponse<T>> =>
  performRequest(
    'DELETE',
    url,
    body ? JSON.stringify(body) : undefined,
    headers
  );

const performRequest = async (
  method: string,
  url: string,
  body: string | FormData,
  headers: Headers
) => {
  const res = await getFetchWrapper().fetch(url, {
    method,
    headers,
    credentials: 'include',
    mode: 'cors',
    body,
  });
  try {
    const data = await res.clone().json();
    return new ApiResponse(res.status, data);
  } catch {
    const error = await res.text();
    return new ApiResponse(res.status, undefined, error);
  }
};
